// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getCookie } from "@insite/mobius/utilities/cookies";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@vanmeter/App/Components/Common/XngZone";
import loadEvents from "@vanmeter/App/Handlers/EventList/LoadEvents";
import updateFields from "@vanmeter/App/Handlers/EventList/UpdateFields";
import { IEventListParams } from "@vanmeter/App/Services/EventListService";
import { CustomAppState, getEventListDataView } from "@vanmeter/App/Store/Reducers";
import { Divider, xScreen } from "@vanmeter/XDL/Utilities";
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const location = getLocation(state);
    return {
        parameters: state.xEventList.params,
        location,
        eventsDataView: getEventListDataView(state),
    };
};

const mapDispatchToProps = {
    loadEvents,
    updateFields,
};

const enum fields {
    title = "title",
    subTitle = "subTitle",
}

interface OwnProps extends PageProps {
    fields: {
        [fields.title]: string;
        [fields.subTitle]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface VmiEventCoursePageStyles {
    container?: InjectableCss;
    contentWrapper?: InjectableCss;
    listWrapper?: InjectableCss;
    titleText?: TypographyProps;
    subTitleText?: TypographyProps;
}
export const vmiEventCoursePageStyles: VmiEventCoursePageStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: 64px;
        `,
    },
    contentWrapper: {
        css: css`
            display: grid;
            grid-template-columns: 1fr minmax(0, 376px);
            ${xScreen("lte", "lg")} {
                grid-template-columns: 1fr minmax(0, 317px);
            }
            ${xScreen("lte", "md")} {
                grid-template-columns: 1fr 1fr;
            }
            ${xScreen("lt", "sm")} {
                grid-template-columns: 1fr;
            }
            grid-gap: 64px 22px;
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            .events-list-pagination {
                margin-bottom: 0 !important;
            }
        `,
    },
    titleText: {
        variant: "h1",
    },
    subTitleText: {
        variant: "h4",
        css: css`
            &:empty {
                display: none;
            }
        `,
    },
};

const styles = vmiEventCoursePageStyles;

const VmiEventCoursePage: React.FunctionComponent<Props> = ({
    nodeId,
    location,
    parameters,
    eventsDataView,
    updateFields,
    loadEvents,
    history,
    id,
    fields,
}) => {
    let firstLoad = false;
    useEffect(() => {
        firstLoad = true;
        const pageSizeCookie = getCookie("EventsList-PageSize");
        const pageSize = pageSizeCookie ? parseInt(pageSizeCookie, 10) : undefined;
        if (location.search) {
            const apiParameters = parseQueryString<IEventListParams>(location.search);
            if (pageSize) {
                apiParameters.pageSize = pageSize;
            }
            updateFields({ ...apiParameters, parentId: nodeId, type: "Replace" });
        } else {
            updateFields({ type: "Initialize", pageSize, parentId: nodeId });
        }
        return () => {
            firstLoad = false;
        };
    }, []);

    useEffect(() => {
        if (!firstLoad) {
            const queryString = qs.stringify({ ...parameters, parentId: nodeId });
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [parameters]);

    useEffect(() => {
        // if this is undefined it means someone changed the filters and we haven't loaded the new collection yet
        if (!firstLoad && !eventsDataView.value && !eventsDataView.isLoading) {
            loadEvents(parameters);
        }
    });

    return (
        <Page>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.contentWrapper}>
                    <StyledWrapper>
                        <Typography {...styles.titleText}>{fields.title}</Typography>
                        <Typography {...styles.subTitleText}>{fields.subTitle}</Typography>
                        <XngZone contentId={id} zoneName="content01" hint="content" />
                    </StyledWrapper>
                    <XngZone contentId={id} zoneName="content02" hint="Right Section" />
                </StyledWrapper>
                <Divider margin="0" direction="horizontal" />
                <StyledWrapper {...styles.listWrapper}>
                    {createWidgetElement("EventsList/EventsListFilters", { fields: {} })}
                    {createWidgetElement("EventsList/AllEvents", { fields: {} })}
                    {createWidgetElement("EventsList/EventsListPagination", {
                        fields: { showResultsPerPage: false, showPagesNumber: true },
                    })}
                    <XngZone contentId={id} zoneName="content03" hint="Extra" />
                </StyledWrapper>
            </StyledWrapper>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(VmiEventCoursePage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "Content",
        // OOTB bug: multi allowed parents not working - needed on VmiEventDetailsPage and VmiEventCoursePage
        // the issue on AddPage.tsx line 311 - should be -> this.props.addingPageUnderType.toLowerCase()
        // allowedParents: ["VmiEventsListPage"],
        allowedChildren: ["Page", "VmiEventDetailsPage", "VmiFolderPage"],
        fieldDefinitions: [
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            HideHeader,
            HorizontalRule,
            MetaDescription,
            MetaKeywords,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            {
                name: fields.title,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.subTitle,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
            },
        ],
    },
};
export default pageModule;
/**
 * @deprecated Use string literal "VmiEventCoursePage" instead of this constant.
 */
export const VmiEventCoursePageContext = "VmiEventCoursePage";
